/**
 * Timezone utility functions for GMT+5 (Pakistan Standard Time)
 * Ensures consistent date/time handling across the entire application
 */

/**
 * Get current date and time in GMT+5 timezone
 * @returns Date object adjusted to GMT+5
 */
export function getGMT5Time(): Date {
  const now = new Date();
  // GMT+5 means 5 hours ahead of UTC
  const gmt5Offset = 5 * 60 * 60 * 1000; // 5 hours in milliseconds
  return new Date(now.getTime() + gmt5Offset + (now.getTimezoneOffset() * 60 * 1000));
}

/**
 * Get current date in GMT+5 timezone in YYYY-MM-DD format
 * @returns String in YYYY-MM-DD format for HTML5 date inputs
 */
export function getCurrentDateGMT5(): string {
  const gmt5Time = getGMT5Time();
  const year = gmt5Time.getFullYear();
  const month = String(gmt5Time.getMonth() + 1).padStart(2, '0');
  const day = String(gmt5Time.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get current date and time in GMT+5 timezone formatted for display
 * @returns String in DD-MMM-YYYY HH:mm format
 */
export function getFormattedDateTimeGMT5(): string {
  const gmt5Time = getGMT5Time();
  const day = String(gmt5Time.getDate()).padStart(2, '0');
  const month = gmt5Time.toLocaleString('en-US', { month: 'short' });
  const year = gmt5Time.getFullYear();
  const hours = String(gmt5Time.getHours()).padStart(2, '0');
  const minutes = String(gmt5Time.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${minutes}`;
}

/**
 * Convert any date to GMT+5 timezone
 * @param date - Date to convert (assumes UTC input)
 * @returns Date object adjusted to GMT+5
 */
export function convertToGMT5(date: Date): Date {
  // If the input date is in UTC, add 5 hours
  const gmt5Offset = 5 * 60 * 60 * 1000; // 5 hours in milliseconds
  return new Date(date.getTime() + gmt5Offset);
}

/**
 * Format a date to DD-MM-YYYY HH:MM using the user's local machine timezone.
 * @param date - Date to format
 * @returns Formatted string
 */
export function formatDateTimeGMT5(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
